import React, { useEffect, useState } from 'react';
import { Flex, Text, Box } from 'rebass/styled-components';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import _, { compact, reject } from 'lodash';
import { isBlank } from 'underscore.string';
import isEmail from 'validator/lib/isEmail';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { OAuthLogin } from 'src/checkout/components/header/oauth-login';
import { ModalNames } from 'src/components/modals';
import errors from 'shared/core/fieldValidationErrorMessages';
import { callNativeMethod } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';

import { DEFAULT_INPUT_MAX_LENGTH } from 'shared/constants';
import { isNullish } from 'shared/utils/type-utils';
import { CheckboxOld, Tooltip } from 'shared/components';
import { mediaQueries } from 'shared/styles';
import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import { DefaultInput, DateInput, PhoneNumberInput } from 'shared/components/text-input-with-label';
import { useCheckout } from 'checkout/context';
import { CheckoutVariants } from 'src/constants/checkout-variants';
import useCheckForRewards from 'shared/hooks/use-check-for-rewards';
import useStores from 'shared/hooks/use-stores';
import useMediaQuery from 'shared/hooks/use-media-query';
import useDebounce from 'shared/hooks/use-debounce';
import { formatBirthdayForRewards } from 'shared/helpers/rewards';
import { isDutchiePlusKioskOrder } from 'shared/helpers/checkout';
import PhotoIdUpload from 'checkout/components/sections/user/photo-id-upload';
import StateSelect from 'checkout/components/sections/user/state-select';
import { VisuallyHidden } from 'src/components/core/visually-hidden';
import { Terms } from '../terms';

/*
 * This feels like a sin and is smelly but is a workaround due to the label shrinking state
 * not getting set automatically in a reliable way which is documented in MUI's
 * docs: https://mui.com/material-ui/react-text-field/#shrink
 * Unfortunately this issue of overlap was being seen in Safari browsers and this
 * hook accounts for setting state for Cart store values being undefined on initial renders
 * - Mack H. 2/8/23
 */
function useLabelShrinkState(guestCustomer) {
  const [shrinkFirstNameLabel, setShrinkFirstNameLabel] = useState(false);
  const [shrinkLastNameLabel, setShrinkLastNameLabel] = useState(false);
  const [shrinkEmailLabel, setShrinkEmailLabel] = useState(false);
  const [shrinkBirthdateLabel, setShrinkBirthdateLabel] = useState(false);
  const [shrinkPhoneLabel, setShrinkPhoneLabel] = useState(false);

  const handleSetUnshrinkWhenNullish = (setter, fields) => {
    if (_.every(fields, isNullish)) {
      setter(false);
    } else {
      setter(true);
    }
  };

  useEffect(() => {
    handleSetUnshrinkWhenNullish(setShrinkFirstNameLabel, [guestCustomer?.firstName]);
  }, [guestCustomer?.firstName]);

  useEffect(() => {
    handleSetUnshrinkWhenNullish(setShrinkLastNameLabel, [guestCustomer?.lastName]);
  }, [guestCustomer?.lastName]);

  useEffect(() => {
    handleSetUnshrinkWhenNullish(setShrinkEmailLabel, [guestCustomer?.email]);
  }, [guestCustomer?.email]);

  useEffect(() => {
    handleSetUnshrinkWhenNullish(setShrinkBirthdateLabel, [
      guestCustomer?.birthMonth,
      guestCustomer?.birthDay,
      guestCustomer?.birthYear,
    ]);
  }, [guestCustomer?.birthMonth, guestCustomer?.birthDay, guestCustomer?.birthYear]);

  useEffect(() => {
    handleSetUnshrinkWhenNullish(setShrinkPhoneLabel, [guestCustomer?.phone]);
  }, [guestCustomer?.phone]);

  return {
    shrinkFirstNameLabel,
    shrinkLastNameLabel,
    shrinkEmailLabel,
    shrinkBirthdateLabel,
    shrinkPhoneLabel,
    setShrinkFirstNameLabel,
    setShrinkLastNameLabel,
    setShrinkEmailLabel,
    setShrinkBirthdateLabel,
    setShrinkPhoneLabel,
  };
}

function useHasRewards(guestCustomer) {
  const debouncedPhone = useDebounce(guestCustomer?.phone, 500);
  const debouncedBirthday = useDebounce(
    [guestCustomer?.birthYear, guestCustomer?.birthMonth, guestCustomer?.birthDay].join('-'),
    500
  );

  const hasRewards = useCheckForRewards(debouncedPhone, formatBirthdayForRewards(debouncedBirthday));

  return hasRewards;
}

function formatBirthdate(guestCustomer) {
  return compact(reject([guestCustomer?.birthMonth, guestCustomer?.birthDay, guestCustomer?.birthYear], isBlank)).join(
    '/'
  );
}

function GuestInfo() {
  const { Cart, UI, Modals } = useStores();
  const Checkout = useCheckout();
  const flags = useFlags();
  const autoLoginOnDuplicateEmail = flags['ecomm.checkout.auto-login-modal-on-duplicate-email.rollout'] ?? false;

  const [showRewardsTooltip, setShowRewardsTooltip] = useState(false);
  const [showEmailRequiredReason, setShowEmailRequiredReason] = useState(false);
  const {
    shrinkFirstNameLabel,
    shrinkLastNameLabel,
    shrinkEmailLabel,
    shrinkBirthdateLabel,
    shrinkPhoneLabel,
    setShrinkFirstNameLabel,
    setShrinkLastNameLabel,
    setShrinkEmailLabel,
    setShrinkBirthdateLabel,
    setShrinkPhoneLabel,
  } = useLabelShrinkState(Cart.order?.guestCustomer);

  const isDutchiePlusKiosk = isDutchiePlusKioskOrder(Cart.orderType, UI.isPlus);

  const { isDelivery, isPickup, order, updateCustomerState } = Cart;
  const { dispensary } = order;
  const { requireEmailAddressForGuestCheckout } = dispensary.storeSettings || {};
  const requirePhotoIdForDelivery = _.get(dispensary, 'requirePhotoIdForDelivery', false);
  const requirePhotoIdForPickup = _.get(dispensary, 'requirePhotoIdForPickup', false);
  const showPhotoId =
    (isDelivery && requirePhotoIdForDelivery && !isDutchiePlusKiosk) ||
    (isPickup && requirePhotoIdForPickup && !isDutchiePlusKiosk);
  const mobile = window.matchMedia(mediaQueries.tablet).matches;

  const isEmailRequiredForCurbsideArrival =
    Cart.orderType === 'curbsidePickup' && Cart.dispensary?.featureFlags?.enableArrivals;

  const showEmailInput =
    (requireEmailAddressForGuestCheckout || isEmailRequiredForCurbsideArrival) && !Cart.saveGuestInfo;

  const belowDesktop = useMediaQuery(mediaQueries.desktop);
  const hasRewards = useHasRewards(Cart.order?.guestCustomer);
  const birthdate = formatBirthdate(Cart.order?.guestCustomer);

  const enableOmniauthLogin = dispensary?.storeSettings?.enableOmniauthLoginCheckout ?? false;

  const displayAnonymousCheckoutDisclaimer =
    isDutchiePlusKiosk && Cart.dispensary?.storeSettings?.displayKioskAnonymousCheckoutDisclaimer;

  const {
    order: {
      guestCustomer: { firstName, lastName, email, phone, stateResidency },
    },
  } = Cart;

  function promoTextContent() {
    let textContent;
    if (enableOmniauthLogin) {
      textContent = `Please log in or create an account with ${dispensary.name} to use a single use promotion code.`;
    } else if (Cart.guestCouponError) {
      textContent = `You must create an account to use a coupon. Enter your ${
        Cart.order.dispensary.storeSettings?.disableGuestDOB
          ? 'birthday, email, and a password'
          : 'email and a password'
      }`;
    } else {
      textContent = 'Create an account for 1-click ordering';
    }

    return textContent;
  }

  useEffect(() => {
    Cart.order.isGuestOrder = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when the tooltip is shown, set a timeout to clear it
  // if the tooltip is hidden before the timeout ends, clear it
  useEffect(() => {
    let timeout = null;
    if (showRewardsTooltip) {
      timeout = setTimeout(() => setShowRewardsTooltip(false), 5000);
    }
    return () => clearTimeout(timeout);
  }, [showRewardsTooltip]);

  useEffect(() => {
    setShowRewardsTooltip(hasRewards);
    updateCustomerState(stateResidency);
  }, [hasRewards, stateResidency, updateCustomerState]);

  // Use the error object reference (not a boolean) as the dep so the effect re-fires on every
  // resubmission. addErrors always creates new error objects via spread, so the reference
  // changes each time even when the message is the same.
  const duplicateEmailError = _.find(
    Checkout.errors,
    (e) => e.path === 'guestCustomer.email' && e.message === errors.email.duplicate
  );

  useEffect(() => {
    if (!duplicateEmailError || !autoLoginOnDuplicateEmail || UI.hideLogin) return;

    if (UI.isMobileEcommApp) {
      callNativeMethod('onPromptLogin');
      return;
    }

    if (UI.isUsingExternalClickHandlerLogin) {
      UI.embeddedEventDispatcher('embed:clickHandler:login');
      return;
    }

    Modals.openModal(ModalNames.login);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateEmailError, autoLoginOnDuplicateEmail]);

  const updateBirthdatePart = (e) => {
    const string = e.target.value;
    const [birthmonth, birthday, birthyear] = _.split(string, '/');
    Cart.updateGuestCustomer('birthMonth', birthmonth);
    Cart.updateGuestCustomer('birthDay', birthday);
    Cart.updateGuestCustomer('birthYear', birthyear);
  };

  const onPhotoUploaded = (photoId) => {
    Cart.updateGuestCustomer('photoId', photoId);
  };

  const onPhotoReset = () => {
    Cart.updateGuestCustomer('photoId', '');
  };

  const onChangeGuestEmail = ({ target }) => {
    Cart.updateGuestCustomer(target.name, target.value);

    if (isEmail(target.value)) {
      setShowEmailRequiredReason(true);
      setTimeout(() => setShowEmailRequiredReason(false), 4000);
    }
  };

  const handleChangeSetGuestInfo = ({ target }) => {
    if (target.checked) {
      if (!Cart.order.guestCustomer) {
        Cart.order.guestCustomer = {};
      }
      Cart.order.guestCustomer.password = '';
      Cart.order.guestCustomer.emailNotifications = false;
      Cart.order.guestCustomer.emailOptIn = false;
      if (Cart.order.guestCustomer.termsOptIn) {
        delete Cart.order.guestCustomer.termsOptIn;
      }
    }
    Cart.saveGuestInfo = target.checked;
  };

  const { variant } = useCheckout();
  const isIsolatedCheckout = variant === CheckoutVariants.isolatedCheckout;

  return (
    <CheckoutLabelGrouping id='guest-customer' data-testid='guest-customer-section'>
      <CheckoutLabel>
        <CheckoutSectionHeading>
          <VisuallyHidden>Customer</VisuallyHidden>
        </CheckoutSectionHeading>
      </CheckoutLabel>

      <CheckoutBody>
        {displayAnonymousCheckoutDisclaimer && (
          <FieldContainer>
            <AnonymousCheckoutDisclaimerContainer>
              <AnonymousCheckoutDisclaimerBox>
                <Text
                  textAlign='left'
                  fontSize='12px'
                  lineHeight='17px'
                  color='var(--dcm-checkout-text-muted-color, #858788)'
                >
                  If you would like to place an order anonymously, please proceed directly to a store associate for
                  assistance.
                </Text>
              </AnonymousCheckoutDisclaimerBox>
            </AnonymousCheckoutDisclaimerContainer>
          </FieldContainer>
        )}
        <FieldContainer>
          <NameContainer>
            <DefaultInput
              id='firstName'
              onValueAfterError={() => Checkout.removeError({ path: 'guestCustomer.firstName' })}
              error={Checkout.getError({ path: 'guestCustomer.firstName' }).error}
              errorMessage={mobile ? 'First name' : Checkout.getError({ path: 'guestCustomer.firstName' }).message}
              name='firstName'
              label='First Name'
              aria-label='First name input'
              data-cy='first-name-input'
              data-test='first-name-input'
              onChange={({ target }) => Cart.updateGuestCustomer(target.name, target.value)}
              onFocus={() => setShrinkFirstNameLabel(true)}
              onBlur={() => setShrinkFirstNameLabel(!isBlank(firstName))}
              value={firstName}
              width='100%'
              mr='15px'
              autoComplete={isDutchiePlusKiosk ? 'off' : 'given-name'}
              InputLabelProps={{ shrink: shrinkFirstNameLabel }}
              inputProps={{ maxLength: DEFAULT_INPUT_MAX_LENGTH }}
              required
            />
            <DefaultInput
              id='lastName'
              onValueAfterError={() => Checkout.removeError({ path: 'guestCustomer.lastName' })}
              error={Checkout.getError({ path: 'guestCustomer.lastName' }).error}
              errorMessage={mobile ? 'Last name' : Checkout.getError({ path: 'guestCustomer.lastName' }).message}
              name='lastName'
              label='Last Name'
              data-cy='last-name-input'
              data-test='last-name-input'
              aria-label='Last name input'
              onChange={({ target }) => Cart.updateGuestCustomer(target.name, target.value)}
              onFocus={() => setShrinkLastNameLabel(true)}
              onBlur={() => setShrinkLastNameLabel(!isBlank(lastName))}
              value={lastName}
              width='100%'
              autoComplete={isDutchiePlusKiosk ? 'off' : 'family-name'}
              InputLabelProps={{ shrink: shrinkLastNameLabel }}
              inputProps={{ maxLength: DEFAULT_INPUT_MAX_LENGTH }}
              required
            />
          </NameContainer>
        </FieldContainer>
        <FieldContainer>
          <PhoneNumberInput
            id='phone'
            onValueAfterError={() => Checkout.removeError({ path: 'guestCustomer.phone' })}
            error={Checkout.getError({ path: 'guestCustomer.phone' }).error}
            errorMessage={Checkout.getError({ path: 'guestCustomer.phone' }).message}
            name='phone'
            width='100%'
            label='Mobile Phone'
            aria-label='Phone number input'
            data-cy='phone-input'
            data-test='phone-input'
            value={Cart.order?.guestCustomer?.phone}
            onChange={({ target }) => Cart.updateGuestCustomer(target.name, target.value)}
            onFocus={() => setShrinkPhoneLabel(true)}
            onBlur={() => setShrinkPhoneLabel(!isBlank(phone))}
            autoComplete={isDutchiePlusKiosk ? 'off' : 'tel'}
            InputLabelProps={{ shrink: shrinkPhoneLabel }}
            required
          />
          <Tooltip
            hideIcon
            leftArrow={!belowDesktop}
            arrowRight={belowDesktop && '255px'}
            visible={showRewardsTooltip}
            bottom={belowDesktop ? '58px' : '-10px'}
            right={belowDesktop ? '82px' : 'auto'}
            left={belowDesktop ? 'auto' : '15px'}
            width={belowDesktop ? '309px' : '309px'}
            mobilePosition={{ right: '-14px' }}
            tooltipCopy={
              <>
                You’re connected to <Bold>{Cart.rewardsProgramDisplayName}</Bold>! Scroll down to view your wallet for
                this order.
              </>
            }
          />
        </FieldContainer>
        {!Cart.order.dispensary.storeSettings?.disableGuestDOB && (
          <FieldContainer>
            <DateInput
              id='birthdate'
              onValueAfterError={() => Checkout.removeError({ match: 'guestCustomer.birth' })}
              error={Checkout.getError({ match: 'guestCustomer.birth' }).error}
              errorMessage={Checkout.getError({ match: 'guestCustomer.birth' }).message}
              name='birthdate'
              value={birthdate}
              aria-label='Birthdate input'
              data-cy='birthdate-input'
              data-test='birthdate-input'
              onChange={updateBirthdatePart}
              onFocus={() => setShrinkBirthdateLabel(true)}
              onBlur={() => setShrinkBirthdateLabel(!isBlank(birthdate))}
              width='100%'
              label='Birthdate'
              autoComplete={isDutchiePlusKiosk ? 'off' : 'bday'}
              InputLabelProps={{ shrink: shrinkBirthdateLabel }}
              required
            />
          </FieldContainer>
        )}
        {showEmailInput && (
          <FieldContainer>
            <Tooltip
              visible={showEmailRequiredReason}
              tooltipCopy={
                <p>
                  Your email address will be used to notify you about <b>updates pertaining to your order</b>.
                </p>
              }
              mainCopy={
                <DefaultInput
                  id='email'
                  onValueAfterError={() => Checkout.removeError({ path: 'guestCustomer.email' })}
                  error={Checkout.getError({ path: 'guestCustomer.email' }).error}
                  errorMessage={Checkout.getError({ path: 'guestCustomer.email' }).message}
                  name='email'
                  label='Email'
                  aria-label='Email input'
                  data-cy='email-input'
                  data-test='email-input'
                  onChange={onChangeGuestEmail}
                  onFocus={() => setShrinkEmailLabel(true)}
                  onBlur={() => setShrinkEmailLabel(!isBlank(email))}
                  value={email}
                  width='480px'
                  autoComplete={isDutchiePlusKiosk ? 'off' : 'email'}
                  InputLabelProps={{ shrink: shrinkEmailLabel }}
                  inputProps={{ maxLength: 300 }}
                  required
                />
              }
              grey
              width='300px'
              height='62px'
              position='absolute'
              top='-9px'
              left='405px'
              leftArrow
              hideIcon
            />
          </FieldContainer>
        )}

        {Cart.requiresCustomerState && (
          <FieldContainer>
            <StateSelect
              error={Checkout.getError({ path: 'customerState' }).error}
              onValueChange={() => Checkout.removeError({ path: 'customerState' })}
              required
            />
          </FieldContainer>
        )}

        {showPhotoId && (
          <FieldContainer extended>
            <PhotoIdUpload
              aria-label='Upload a photo I.D.'
              guest
              id='guestPhoto'
              error={Checkout.getError({ path: 'guestCustomer.photoId' }).error}
              value={Cart.order.guestCustomer?.photoId}
              onValueAfterError={() => Checkout.removeError({ path: 'guestCustomer.photoId' })}
              onPhotoUploaded={onPhotoUploaded}
              onPhotoReset={onPhotoReset}
              required
            />
          </FieldContainer>
        )}
        {!UI.isUsingExternalClickHandlerLogin && !UI.hideLogin && (!enableOmniauthLogin || !isIsolatedCheckout) && (
          <FieldContainer>
            <CheckboxOld
              name='saveGuestInfo'
              label='Save to fast track your next checkout'
              aria-label='Save to fast track your next checkout'
              data-cy='save-guest-info'
              data-test='save-guest-info'
              data-testid='save-guest-info'
              checked={Cart.saveGuestInfo}
              onChange={handleChangeSetGuestInfo}
              color='var(--dcm-checkout-text-color, #6b6f73)'
            />
          </FieldContainer>
        )}
        {Cart.saveGuestInfo && (
          <FieldContainer>
            <SaveInfoContainer>
              <Text
                color={Cart.guestCouponError ? '#e25241' : 'var(--dcm-checkout-text-color, #606369)'}
                fontSize='13px'
                mb='17px'
              >
                {promoTextContent()}
              </Text>
              {Cart.order.dispensary.storeSettings?.disableGuestDOB && (
                <DateInput
                  onValueAfterError={() => Checkout.removeError({ match: 'guestCustomer.birth' })}
                  error={Checkout.getError({ match: 'guestCustomer.birth' }).error}
                  errorMessage={Checkout.getError({ match: 'guestCustomer.birth' }).message}
                  name='birthdate'
                  value={birthdate}
                  onChange={updateBirthdatePart}
                  onFocus={() => setShrinkBirthdateLabel(true)}
                  onBlur={() => setShrinkBirthdateLabel(!isBlank(birthdate))}
                  width='100%'
                  label='Birthdate'
                  aria-label='Birthdate input'
                  data-cy='account-dob'
                  data-test='account-dob'
                  mb='19px'
                  autoComplete='bday'
                  InputLabelProps={{ shrink: shrinkBirthdateLabel }}
                />
              )}

              {enableOmniauthLogin ? (
                <OAuthLogin />
              ) : (
                !(requireEmailAddressForGuestCheckout || isEmailRequiredForCurbsideArrival) && (
                  <DefaultInput
                    id='email2'
                    onValueAfterError={() => Checkout.removeError({ path: 'guestCustomer.email' })}
                    error={Checkout.getError({ path: 'guestCustomer.email' }).error}
                    errorMessage={Checkout.getError({ path: 'guestCustomer.email' }).message}
                    name='email'
                    label='Email'
                    aria-label='Email input'
                    data-cy='account-email'
                    data-test='account-email'
                    data-testid='account-email'
                    onChange={({ target }) => Cart.updateGuestCustomer(target.name, target.value)}
                    onFocus={() => setShrinkEmailLabel(true)}
                    onBlur={() => setShrinkEmailLabel(!isBlank(email))}
                    value={email}
                    width='100%'
                    mb='19px'
                    autoComplete='email'
                    InputLabelProps={{ shrink: shrinkEmailLabel }}
                  />
                )
              )}
              {!enableOmniauthLogin && (
                <DefaultInput
                  id='password'
                  onValueAfterError={() => Checkout.removeError({ path: 'guestCustomer.password' })}
                  error={Checkout.getError({ path: 'guestCustomer.password' }).error}
                  errorMessage={Checkout.getError({ path: 'guestCustomer.password' }).message}
                  name='password'
                  type='password'
                  label='Password'
                  aria-label='Password input'
                  data-cy='account-password'
                  data-test='account-password'
                  data-testid='account-password'
                  onChange={({ target }) => Cart.updateGuestCustomer(target.name, target.value)}
                  value={Cart.order?.guestCustomer?.password}
                  width='100%'
                  mb='19px'
                  autoComplete='new-password'
                />
              )}
              {!enableOmniauthLogin && <Terms />}
            </SaveInfoContainer>
          </FieldContainer>
        )}
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(GuestInfo);

const FieldContainer = styled(({ ...props }) => <Flex {...props} />)`
  max-width: ${(props) => (props.extended ? '440px' : '390px')};
  margin-bottom: 17px;
  width: 100%;

  @media ${mediaQueries.phone} {
    flex-wrap: wrap;
  }

  &:last-of-type {
    margin-bottom: 0px;
    padding-top: 1px;
  }
`;

const Bold = styled.span`
  font-weight: bold;
`;

const NameContainer = styled(Flex)`
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const SaveInfoContainer = styled.div`
  background-color: var(--dcm-checkout-save-info-bg, #f0f3f6);
  border-radius: 8px;
  max-width: 389px;
  padding: 22px 22px 25px 22px;
  width: 100%;
`;

const AnonymousCheckoutDisclaimerBox = styled(Box)`
  background-color: var(--dcm-checkout-save-info-bg, #f0f3f6);
  border-radius: 7px;
  padding: 12px;
`;

const AnonymousCheckoutDisclaimerContainer = styled.div`
  width: 100%;
  margin-top: -6px;

  @media ${mediaQueries.largePhone} {
    margin-top: 0;
  }
`;
